import React, { useState } from 'react';
import classNames from 'classnames';
import styles from './Sidebar.module.scss';
import { Icon } from 'Components';
import {
	NavItemConfig,
	mainNavItems,
	bottomNavItems
} from 'LearningManager/constants/Sidebar.constants';
import { useStores } from 'Hooks/useStore';

interface SidebarProps {
	activeKey?: string;
	onNavigate?: (key: string, path?: string) => void;
	onCollapse?: (collapsed: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
	activeKey = 'courses',
	onNavigate,
	onCollapse
}) => {
	const [collapsed, setCollapsed] = useState(false);
	const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({
		manage: false,
		reports: false,
		modules: false
	});

	const { authStore } = useStores();

	const handleToggleCollapse = () => {
		const next = !collapsed;
		setCollapsed(next);
		onCollapse?.(next);
	};

	const toggleMenu = (key: string) => {
		if (collapsed) return;
		setOpenMenus((prev) => ({ ...prev, [key]: !prev[key] }));
	};

	const handleItemClick = (
		item: NavItemConfig | Omit<NavItemConfig, 'icon' | 'children'>
	) => {
		if ('children' in item && item.children?.length) {
			toggleMenu(item.key);
		} else if (onNavigate) {
			onNavigate(item.key, item.path);
		}
	};

	const renderNavItem = (item: NavItemConfig) => {
		const hasChildren = 'children' in item && !!item.children?.length;
		const isOpen = openMenus[item.key];
		const isActive = activeKey === item.key;

		return (
			<div key={item.key}>
				<div
					className={classNames(styles.navItem, {
						[styles['navItem--active']]: isActive
					})}
					onClick={() => handleItemClick(item)}
				>
					<span
						className={classNames(styles.navItem__icon, {
							[styles['navItem__label-color']]: isOpen
						})}
					>
						{item.icon}
					</span>
					<span
						className={classNames(styles.navItem__label, {
							[styles['navItem__label-color']]: isOpen
						})}
					>
						{item.label}
					</span>
					{hasChildren && (
						<Icon
							type="chevronDown"
							role="icon"
							className={classNames(styles.navItem__chevron, {
								[styles['navItem__chevron--open']]: isOpen
							})}
						/>
					)}
				</div>

				{hasChildren && (
					<div
						className={styles.subMenu}
						style={{
							maxHeight: isOpen
								? `${item.children!.length * 40}px`
								: '0px'
						}}
					>
						{item.children!.map((child) => (
							<div
								key={child.key}
								className={classNames(styles.navItem, {
									[styles['navItem--active']]:
										activeKey === child.key
								})}
								onClick={() =>
									onNavigate?.(child.key, child.path)
								}
							>
								{child.icon && (
									<span className={styles.navItem__icon}>
										{child.icon}
									</span>
								)}
								<span className={styles.navItem__label}>
									{child.label}
								</span>
							</div>
						))}
					</div>
				)}
			</div>
		);
	};

	return (
		<aside
			className={classNames(styles.sidebar, {
				[styles['sidebar--collapsed']]: collapsed
			})}
		>
			<div className={styles.logo}>
				<div className={styles.logo__icon}>
					<Icon type="logoIcon" role="img" />
				</div>
				<span className={styles.logo__name}>Pyramid</span>
				<div
					className={styles.logo__collapse}
					onClick={handleToggleCollapse}
				>
					<Icon type="collapseIcon" role="img" />
				</div>
			</div>

			{/* Main Nav */}
			<nav className={styles.nav}>
				{mainNavItems.map((item) => renderNavItem(item))}
			</nav>

			{/* Bottom Nav */}
			<div className={styles.bottomSection}>
				<div className={styles.bottomNav}>
					{bottomNavItems.map((item) => renderNavItem(item))}
				</div>

				{/* User Row */}
				{authStore.currentUser && (
					<div className={styles.userRow}>
						<div className={styles.userRow__avatar}>
							{authStore.currentUser.firstName.charAt(0)}
							{authStore.currentUser.lastName &&
								authStore.currentUser.lastName.charAt(0)}
						</div>
						<div className={styles.userRow__info}>
							<div className={styles.userRow__name}>
								{authStore.currentUser.firstName}{' '}
								{authStore.currentUser.lastName}
							</div>
							{authStore.currentUser.primarySystemRole.role.includes(
								'admin'
							) ? (
								<div className={styles.userRow__role}>
									Administrator
								</div>
							) : (
								<div className={styles.userRow__role}>
									{
										authStore.currentUser.primarySystemRole
											.role
									}
								</div>
							)}
						</div>
						<Icon
							type="optionIcon"
							role="img"
							className={styles.userRow__dots}
						/>
					</div>
				)}
			</div>
		</aside>
	);
};

export default Sidebar;
